import { Breakpoint, Button, ButtonProps } from '@mui/material';

import { ManagedAxleDialog, ManagedAxleDialogProps } from '../../dialogs/ManagedAxleDialog';
import { Children, ReturnComponent } from '../../utils/reactTypes';
import { AxleDialog, AxleDialogActions, AxleDialogContent } from './AxleDialog';

interface IAxleConfirmDialogProps {
    children: Children;
    swapButtonOrder?: boolean;
    cancelCta?: string;
    cancelButtonProps?: Omit<ButtonProps, 'onClick'>;
    close: () => void;
    confirmCta: string;
    confirmButtonEnabled?: boolean;
    confirmButtonProps?: Omit<ButtonProps, 'onClick'>;
    onConfirm: () => void;
    width?: Breakpoint;
    title?: string;
}

type AxleConfirmDialogContentProps = Omit<IAxleConfirmDialogProps, 'width' | 'title'> & {
    cancelCta: string;
    confirmButtonEnabled: boolean;
};

const AxleConfirmDialogContent = ({
    children,
    cancelCta,
    cancelButtonProps = {},
    swapButtonOrder = false,
    close,
    confirmCta,
    confirmButtonEnabled,
    confirmButtonProps = {},
    onConfirm,
}: AxleConfirmDialogContentProps) => {
    const cancelButton = (
        <Button
            key={'cancel'}
            fullWidth
            variant='outlined'
            color='primary'
            {...cancelButtonProps}
            onClick={close}
        >
            {cancelCta}
        </Button>
    );
    const confirmButton = (
        <Button
            key={'confirm'}
            fullWidth
            variant='outlined'
            color='error'
            disabled={!confirmButtonEnabled}
            {...confirmButtonProps}
            onClick={onConfirm}
        >
            {confirmCta}
        </Button>
    );

    return (
        <>
            <AxleDialogContent>{children}</AxleDialogContent>
            <AxleDialogActions>
                {swapButtonOrder ? [confirmButton, cancelButton] : [cancelButton, confirmButton]}
            </AxleDialogActions>
        </>
    );
};

export type ManagedAxleConfirmDialogProps = {
    dataTestId: string;
    message: string | Children;
} & Omit<IAxleConfirmDialogProps, 'children' | 'close'> &
    ManagedAxleDialogProps;

// This confirmation dialog only supports a basic message, not arbitrary child components.
export const ManagedAxleConfirmDialog = ({
    dataTestId,
    onClose,
    title = 'Confirm',
    message,
    confirmCta,
    swapButtonOrder,
    confirmButtonProps,
    cancelCta = 'Cancel',
    cancelButtonProps,
    onConfirm,
    confirmButtonEnabled = true,
    width = 'sm',
}: ManagedAxleConfirmDialogProps) => {
    return (
        <ManagedAxleDialog dataTestId={dataTestId} title={title} width={width} onClose={onClose}>
            {({ close }) => (
                <AxleConfirmDialogContent
                    swapButtonOrder={swapButtonOrder}
                    cancelCta={cancelCta}
                    cancelButtonProps={cancelButtonProps}
                    close={close}
                    confirmCta={confirmCta}
                    confirmButtonEnabled={confirmButtonEnabled}
                    confirmButtonProps={confirmButtonProps}
                    onConfirm={() => {
                        onConfirm();
                        close();
                    }}
                >
                    {message}
                </AxleConfirmDialogContent>
            )}
        </ManagedAxleDialog>
    );
};

export const AxleConfirmDialog = ({
    isOpen,
    close,
    children,
    title = 'Confirm',
    confirmCta,
    cancelCta = 'Cancel',
    swapButtonOrder,
    cancelButtonProps,
    onConfirm,
    confirmButtonEnabled = true,
    confirmButtonProps,
    width = 'sm',
}: IAxleConfirmDialogProps & {
    isOpen: boolean;
    close: () => void;
}): ReturnComponent => {
    return (
        <AxleDialog
            dataTestId='confirm-dialog'
            isOpen={isOpen}
            close={close}
            title={title}
            width={width}
        >
            <AxleConfirmDialogContent
                swapButtonOrder={swapButtonOrder}
                cancelCta={cancelCta}
                cancelButtonProps={cancelButtonProps}
                close={close}
                confirmCta={confirmCta}
                confirmButtonEnabled={confirmButtonEnabled}
                confirmButtonProps={confirmButtonProps}
                onConfirm={onConfirm}
            >
                {children}
            </AxleConfirmDialogContent>
        </AxleDialog>
    );
};
